import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Button, Tag, Row, Col, Modal, Input, message, Spin, Empty, Typography, Divider, Progress, Table, Tabs } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  ProjectOutlined,
  TeamOutlined,
  PlusOutlined,
  EyeOutlined,
  WarningOutlined,
  SafetyOutlined,
  FolderOutlined,
  FileTextOutlined,
  RightOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

const DetailsWrapper = styled.div`
  padding: 24px;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;

    .header-left {
      display: flex;
      align-items: center;
      gap: 16px;

      .back-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border-radius: 8px;
        border: 1px solid #d9d9d9;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          border-color: #01d9a9;
          color: #01d9a9;
        }
      }

      .header-title {
        h2 {
          margin: 0;
          font-size: 24px;
          color: #014c75;
        }
        .engagement-id {
          font-size: 12px;
          color: #8c8c8c;
        }
      }
    }

    .header-actions {
      display: flex;
      gap: 12px;
    }
  }

  .main-card {
    border-radius: 12px;
    margin-bottom: 24px;
  }

  .status-banner {
    padding: 16px 24px;
    border-radius: 8px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 12px;

    &.pending {
      background: #fff7e6;
      border: 1px solid #ffd591;
    }

    &.active {
      background: #f6ffed;
      border: 1px solid #b7eb8f;
    }

    &.closure_requested {
      background: #fffbe6;
      border: 1px solid #ffe58f;
    }

    &.closed {
      background: #f5f5f5;
      border: 1px solid #d9d9d9;
    }

    &.rejected {
      background: #fff2f0;
      border: 1px solid #ffccc7;
    }

    .status-text {
      font-size: 14px;
      color: #595959;
    }
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #014c75;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .health-section {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 24px;

    .health-card {
      padding: 20px;
      border-radius: 12px;
      background: #fafafa;
      text-align: center;

      .health-label {
        font-size: 13px;
        color: #8c8c8c;
        margin-bottom: 8px;
      }

      .health-value {
        font-size: 18px;
        font-weight: 600;

        &.good {
          color: #52c41a;
        }

        &.warning {
          color: #faad14;
        }

        &.critical {
          color: #ff4d4f;
        }
      }

      .health-icon {
        font-size: 24px;
        margin-bottom: 8px;
      }
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  .info-grid-4 {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  .info-item {
    .info-label {
      font-size: 13px;
      color: #8c8c8c;
      margin-bottom: 4px;
    }

    .info-value {
      font-size: 15px;
      color: #262626;
      font-weight: 500;
    }
  }

  .spend-analysis {
    background: linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%);
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;

    .spend-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      .spend-title {
        font-size: 16px;
        font-weight: 600;
        color: #014c75;
      }

      .spend-percentage {
        font-size: 24px;
        font-weight: 700;
        
        &.good { color: #52c41a; }
        &.warning { color: #faad14; }
        &.critical { color: #ff4d4f; }
      }
    }

    .spend-details {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      margin-top: 16px;

      .spend-item {
        text-align: center;
        padding: 12px;
        background: white;
        border-radius: 8px;

        .spend-label {
          font-size: 12px;
          color: #8c8c8c;
        }

        .spend-value {
          font-size: 18px;
          font-weight: 600;
          color: #014c75;
        }
      }
    }
  }

  .risk-compliance-section {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-bottom: 24px;

    .risk-card, .compliance-card {
      padding: 20px;
      border-radius: 12px;
      border: 1px solid #f0f0f0;

      .card-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        font-weight: 600;
        font-size: 15px;
      }

      .card-content {
        color: #595959;
        font-size: 14px;
      }

      .item-list {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          align-items: center;
          gap: 8px;

          &:last-child {
            border-bottom: none;
          }
        }
      }

      .no-items {
        color: #52c41a;
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }

    .risk-card {
      background: #fff9f0;

      .card-header {
        color: #d46b08;
      }
    }

    .compliance-card {
      background: #f0fff4;

      .card-header {
        color: #389e0d;
      }
    }
  }

  .action-section {
    background: linear-gradient(135deg, #014c75 0%, #01d9a9 100%);
    border-radius: 12px;
    padding: 32px;
    margin-bottom: 24px;
    text-align: center;

    .action-title {
      font-size: 20px;
      font-weight: 600;
      color: white;
      margin-bottom: 8px;
    }

    .action-description {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.85);
      margin-bottom: 24px;
    }

    .action-buttons {
      display: flex;
      justify-content: center;
      gap: 16px;
    }
  }

  .projects-section, .jobs-section {
    margin-bottom: 24px;

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
    }

    .empty-state {
      text-align: center;
      padding: 40px;
      background: #fafafa;
      border-radius: 12px;
      color: #8c8c8c;
    }
  }

  .project-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 16px;
  }

  .project-card {
    background: white;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    padding: 16px;
    transition: all 0.3s;
    cursor: pointer;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      border-color: #01d9a9;
    }

    .project-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      .project-title {
        font-size: 16px;
        font-weight: 600;
        color: #014c75;
        margin: 0;
        flex: 1;
      }
      
      .project-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }

    .project-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;

      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;
        
        .meta-label {
          font-size: 12px;
          color: #8c8c8c;
        }

        .meta-value {
          font-size: 13px;
          font-weight: 500;
          color: #262626;
        }
      }
    }
  }

  .jobs-tabs {
    .ant-tabs-nav {
      margin-bottom: 16px;
    }
  }

  .job-table {
    .ant-table-thead > tr > th {
      background: #fafafa;
    }
  }

  .disabled-section {
    opacity: 0.5;
    pointer-events: none;
    position: relative;

    .disabled-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      border-radius: 12px;

      .overlay-message {
        background: #fff7e6;
        padding: 12px 24px;
        border-radius: 8px;
        border: 1px solid #ffd591;
        color: #d46b08;
        font-weight: 500;
      }
    }
  }

  .budget-progress {
    margin-top: 16px;
  }

  .locations-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .contracts-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 20px;

    .stat-box {
      background: linear-gradient(135deg, #f8fdff 0%, #f0f9ff 100%);
      border: 1px solid #e6f4ff;
      border-radius: 12px;
      padding: 20px;

      .stat-label {
        font-size: 10px;
        font-weight: 700;
        color: #8c8c8c;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        margin-bottom: 8px;
      }

      .stat-value {
        font-size: 26px;
        font-weight: 700;
        color: #014c75;
        line-height: 1;
        margin-bottom: 4px;
      }

      .stat-sub {
        font-size: 12px;
        color: #01d9a9;
        font-weight: 500;
      }
    }
  }

  .talent-sub-table {
    padding: 8px 0;
    background: #fafafa;
    border-radius: 8px;
  }
`;

const statusColors = {
  pending_approval: "orange",
  active: "green",
  closure_requested: "gold",
  closed: "default",
};

const statusLabels = {
  pending_approval: "Pending Approval",
  active: "Active",
  closure_requested: "Closure Requested",
  closed: "Closed",
};

const projectStatusColors = {
  pending_approval: "orange",
  active: "green",
  rejected: "red",
  completed: "blue",
};

const jobStatusColors = {
  open: "blue",
  in_progress: "processing",
  closed: "default",
  filled: "green",
  cancelled: "red",
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "₹0";
  return `₹${parseFloat(value).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const CustomerEngagementDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closureModalVisible, setClosureModalVisible] = useState(false);
  const [closureReason, setClosureReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [complianceDocs, setComplianceDocs] = useState([]);
  const [hiringRecords, setHiringRecords] = useState([]);

  const fetchEngagement = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_CONST.GET_ENGAGEMENT}/${id}?includeRelated=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEngagement(response.data.engagement);
    } catch (error) {
      console.error("Error fetching engagement:", error);
      message.error("Failed to load engagement details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchComplianceDocs = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.COMPLIANCE_DOCUMENTS_LIST,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComplianceDocs(response.data?.documents || response.data || []);
    } catch (error) {
      console.error("Error fetching compliance docs:", error);
    }
  }, []);

  const fetchHiringRecords = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_HIRING_RECORDS,
        { status: "active" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHiringRecords(response.data.hiringRecords || []);
    } catch (error) {
      console.error("Error fetching hiring records:", error);
    }
  }, []);

  useEffect(() => {
    fetchEngagement();
    fetchComplianceDocs();
    fetchHiringRecords();
  }, [fetchEngagement, fetchComplianceDocs, fetchHiringRecords]);

  const handleRequestClosure = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.REQUEST_ENGAGEMENT_CLOSURE}/${id}/request-closure`,
        { reason: closureReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Closure request submitted successfully");
      setClosureModalVisible(false);
      fetchEngagement();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to submit closure request");
    } finally {
      setSubmitting(false);
    }
  };

  const getBudgetProgress = () => {
    const total = parseFloat(engagement?.totalBudget || 0);
    if (!total) return 0;
    const spent = parseFloat(engagement.budgetSpent || 0);
    return Math.round((spent / total) * 100);
  };

  const getBudgetStatus = () => {
    const progress = getBudgetProgress();
    if (progress >= 100) return "critical";
    if (progress >= 80) return "warning";
    return "good";
  };

  const getRisks = () => {
    const risks = [];

    if (engagement?.health?.duration === "critical") {
      risks.push("Engagement is overdue");
    } else if (engagement?.health?.duration === "warning") {
      risks.push("Engagement ending within 30 days");
    }
    if (engagement?.health?.budget === "critical") {
      risks.push("Budget has been exceeded");
    } else if (engagement?.health?.budget === "warning") {
      risks.push("Budget utilization above 80%");
    }
    if (engagement?.totalJobs === 0 && engagement?.status === "active") {
      risks.push("No job requirements created yet");
    }

    // Contract expiry risk based on engagement end date
    if (engagement?.expectedEndDate) {
      const endDate = new Date(engagement.expectedEndDate);
      const today = new Date();
      const daysUntilEnd = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntilEnd < 0) {
        risks.push("Contract end date has passed");
      } else if (daysUntilEnd <= 30) {
        risks.push(`Contract expiring in ${daysUntilEnd} day${daysUntilEnd === 1 ? "" : "s"}`);
      }
    }

    // Unsigned compliance docs as risk
    const unsignedDocs = complianceDocs.filter((d) => d.status !== "signed");
    if (unsignedDocs.length > 0) {
      risks.push(`${unsignedDocs.length} compliance document${unsignedDocs.length > 1 ? "s" : ""} pending signature`);
    }

    return risks;
  };

  const getComplianceStatus = () => {
    const issues = [];

    if (complianceDocs.length === 0) {
      return issues;
    }

    const byType = {};
    complianceDocs.forEach((doc) => {
      if (!byType[doc.docType]) byType[doc.docType] = { total: 0, signed: 0 };
      byType[doc.docType].total++;
      if (doc.status === "signed") byType[doc.docType].signed++;
    });

    Object.entries(byType).forEach(([docType, counts]) => {
      const { total, signed } = counts;
      if (signed < total) {
        const label = docType
          ? docType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
          : "Document";
        issues.push(`${label}: ${total - signed} of ${total} unsigned`);
      }
    });

    return issues;
  };

  const jobColumns = [
    {
      title: "Job ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (id) => <Text code style={{ fontSize: 12 }}>{id}</Text>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => <Text strong>{role}</Text>,
    },
    {
      title: "Experience",
      dataIndex: "experienceRange",
      key: "experienceRange",
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={jobStatusColors[status] || "default"}>
          {status?.replace("_", " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Positions",
      dataIndex: "noOfOpenings",
      key: "noOfOpenings",
      width: 90,
      align: "center",
    },
    {
      title: "Project",
      dataIndex: "projectId",
      key: "projectId",
      width: 120,
      render: (projectId) => projectId ? <Tag color="purple">{projectId}</Tag> : <Text type="secondary">Direct</Text>,
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/customer/job/${record.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <DetailsWrapper>
        <div style={{ textAlign: "center", padding: "100px 0" }}>
          <Spin size="large" />
        </div>
      </DetailsWrapper>
    );
  }

  if (!engagement) {
    return (
      <DetailsWrapper>
        <Empty description="Engagement not found" />
      </DetailsWrapper>
    );
  }

  const isActive = engagement.status === "active";
  const isPending = engagement.status === "pending_approval";
  const isRejected = engagement.status === "rejected";
  const projects = engagement.projects || [];
  const projectJobs = engagement.projectJobs || [];
  const directJobs = engagement.directJobs || [];
  const allJobs = [...projectJobs, ...directJobs];
  const risks = getRisks();
  const complianceIssues = getComplianceStatus();

  const allJobIds = new Set(allJobs.map((j) => j.id));
  const engagementHiringRecords = hiringRecords.filter((hr) => allJobIds.has(hr.jobId));
  const contractStats = {
    activeContracts: engagementHiringRecords.length,
    activeTalents: new Set(engagementHiringRecords.map((hr) => hr.talentId)).size,
    monthlySpend: engagementHiringRecords.reduce((s, hr) => s + (parseFloat(hr.monthlyRate) || 0), 0),
    totalBilled: engagementHiringRecords.reduce((s, hr) => s + (parseFloat(hr.totalBilled) || 0), 0),
  };
  const contractJobGroups = Object.values(
    engagementHiringRecords.reduce((acc, hr) => {
      if (!acc[hr.jobId]) acc[hr.jobId] = { jobId: hr.jobId, jobTitle: hr.jobTitle, records: [] };
      acc[hr.jobId].records.push(hr);
      return acc;
    }, {})
  );

  return (
    <DetailsWrapper>
      <div className="header">
        <div className="header-left">
          <div className="back-btn" onClick={() => navigate("/customer/engagements")}>
            <ArrowLeftOutlined />
          </div>
          <div className="header-title">
            <h2>{engagement.title}</h2>
            <span className="engagement-id">{engagement.id}</span>
          </div>
        </div>
        <div className="header-actions">
          {(isPending || isRejected) && (
            <Button icon={<EditOutlined />} onClick={() => navigate(`/customer/edit-engagement/${id}`)}>
              {isRejected ? "Edit & Resubmit" : "Edit"}
            </Button>
          )}
          {isActive && (
            <Button danger icon={<CloseCircleOutlined />} onClick={() => setClosureModalVisible(true)}>
              Request Closure
            </Button>
          )}
        </div>
      </div>

      {isRejected && engagement.rejectionReason && (
        <div style={{
          background: "#fff2f0",
          border: "1px solid #ffccc7",
          borderRadius: 8,
          padding: "16px 20px",
          marginBottom: 24,
          display: "flex",
          alignItems: "flex-start",
          gap: 12
        }}>
          <WarningOutlined style={{ color: "#cf1322", fontSize: 20 }} />
          <div>
            <div style={{ fontWeight: 600, color: "#cf1322", marginBottom: 4 }}>Rejection Reason</div>
            <div style={{ color: "#595959" }}>{engagement.rejectionReason}</div>
          </div>
        </div>
      )}

      <Card className="main-card">
        <div className={`status-banner ${engagement.status.replace("_", "")}`}>
          <Tag color={statusColors[engagement.status]} style={{ fontSize: 14, padding: "4px 12px" }}>
            {statusLabels[engagement.status]}
          </Tag>
          <span className="status-text">
            {isPending && "Your engagement is pending admin approval. You cannot add jobs until it's approved."}
            {isActive && "Your engagement is active. You can now add projects and jobs."}
            {isRejected && "Your engagement has been rejected. Please review the reason and resubmit."}
            {engagement.status === "closure_requested" && "Closure has been requested. Awaiting admin approval."}
            {engagement.status === "closed" && "This engagement has been closed."}
          </span>
        </div>

        <Row gutter={24}>
          <Col span={16}>
            <div className="section-title">
              <FileTextOutlined /> Engagement Summary
            </div>
            <div className="info-grid-4">
              <div className="info-item">
                <div className="info-label">Type</div>
                <div className="info-value">{engagement.engagementType || "-"}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Duration</div>
                <div className="info-value">
                  {engagement.expectedDuration} {engagement.durationUnit || "months"}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Start Date</div>
                <div className="info-value">{formatDate(engagement.actualStartDate || engagement.expectedStartDate)}</div>
              </div>
              <div className="info-item">
                <div className="info-label">End Date</div>
                <div className="info-value">{formatDate(engagement.expectedEndDate)}</div>
              </div>
            </div>

            {engagement.description && (
              <div style={{ marginTop: 16 }}>
                <div className="info-label">Description</div>
                <Paragraph ellipsis={{ rows: 2, expandable: true }}>{engagement.description}</Paragraph>
              </div>
            )}
          </Col>

          <Col span={8}>
            {isActive && engagement.health && (
              <div className="health-section" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                <div className="health-card">
                  <ClockCircleOutlined 
                    className="health-icon" 
                    style={{ color: engagement.health.duration === "good" ? "#52c41a" : engagement.health.duration === "warning" ? "#faad14" : "#ff4d4f" }} 
                  />
                  <div className="health-label">Timeline</div>
                  <div className={`health-value ${engagement.health.duration}`}>
                    {engagement.health.duration === "good" && "On Track"}
                    {engagement.health.duration === "warning" && "Ending Soon"}
                    {engagement.health.duration === "critical" && "Overdue"}
                  </div>
                </div>
                <div className="health-card">
                  <DollarOutlined 
                    className="health-icon" 
                    style={{ color: engagement.health.budget === "good" ? "#52c41a" : engagement.health.budget === "warning" ? "#faad14" : "#ff4d4f" }} 
                  />
                  <div className="health-label">Budget</div>
                  <div className={`health-value ${engagement.health.budget}`}>
                    {engagement.health.budget === "good" && "On Track"}
                    {engagement.health.budget === "warning" && "80%+ Used"}
                    {engagement.health.budget === "critical" && "Exceeded"}
                  </div>
                </div>
                <div className="health-card">
                  <CheckCircleOutlined 
                    className="health-icon" 
                    style={{ color: engagement.health.overall === "good" ? "#52c41a" : engagement.health.overall === "warning" ? "#faad14" : "#ff4d4f" }} 
                  />
                  <div className="health-label">Overall</div>
                  <div className={`health-value ${engagement.health.overall}`}>
                    {engagement.health.overall === "good" && "Healthy"}
                    {engagement.health.overall === "warning" && "Attention"}
                    {engagement.health.overall === "critical" && "Critical"}
                  </div>
                </div>
              </div>
            )}
          </Col>
        </Row>

        <Divider />

        <div className="spend-analysis">
          <div className="spend-header">
            <span className="spend-title">
              <DollarOutlined style={{ marginRight: 8 }} />
              Spend Analysis
            </span>
            <span className={`spend-percentage ${getBudgetStatus()}`}>
              {getBudgetProgress()}% Used
            </span>
          </div>
          <Progress
            percent={getBudgetProgress()}
            strokeColor={getBudgetProgress() >= 100 ? "#ff4d4f" : getBudgetProgress() >= 80 ? "#faad14" : "#52c41a"}
            showInfo={false}
            size="small"
          />
          <div className="spend-details">
            <div className="spend-item">
              <div className="spend-label">Total Budget</div>
              <div className="spend-value">{formatCurrency(engagement.totalBudget || 0)}</div>
            </div>
            <div className="spend-item">
              <div className="spend-label">Remaining</div>
              <div className="spend-value">
                {formatCurrency(Math.max(0, parseFloat(engagement.totalBudget || 0) - parseFloat(engagement.budgetSpent || 0)))}
              </div>
            </div>
            <div className="spend-item">
              <div className="spend-label">Spent Till Now</div>
              <div className="spend-value">{formatCurrency(engagement.budgetSpent)}</div>
            </div>
          </div>
        </div>

        <div className="risk-compliance-section">
          <div className="risk-card">
            <div className="card-header">
              <WarningOutlined /> Risks Identified
            </div>
            <div className="card-content">
              {risks.length > 0 ? (
                <ul className="item-list">
                  {risks.map((risk, idx) => (
                    <li key={idx}>
                      <WarningOutlined style={{ color: "#faad14" }} /> {risk}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-items">
                  <CheckCircleOutlined /> No risks identified
                </div>
              )}
            </div>
          </div>

          <div className="compliance-card">
            <div className="card-header">
              <SafetyOutlined /> Compliance Status
            </div>
            <div className="card-content">
              {complianceIssues.length > 0 ? (
                <ul className="item-list">
                  {complianceIssues.map((issue, idx) => (
                    <li key={idx}>
                      <CloseCircleOutlined style={{ color: "#ff4d4f" }} /> {issue}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-items">
                  <CheckCircleOutlined /> All compliance checks passed
                </div>
              )}
            </div>
          </div>
        </div>

        {engagement.preferredLocations?.length > 0 && (
          <>
            <div className="section-title">
              <EnvironmentOutlined /> Preferred Locations
            </div>
            <div className="locations-list">
              {engagement.preferredLocations.map((loc, idx) => (
                <Tag key={idx} color="blue" icon={<EnvironmentOutlined />}>
                  {loc}
                </Tag>
              ))}
            </div>
            <Divider />
          </>
        )}
      </Card>

      <div className={isActive ? "" : "disabled-section"} style={{ position: "relative" }}>
        {!isActive && (
          <div className="disabled-overlay">
            <div className="overlay-message">
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              Actions available only when engagement is ACTIVE
            </div>
          </div>
        )}

        <div className="action-section">
          <div className="action-title">Build Your Team</div>
          <div className="action-description">
            Create projects to organize work or post job requirements directly under this engagement.
          </div>
          <div className="action-buttons">
            <Button 
              size="large" 
              icon={<ProjectOutlined />}
              style={{ background: "white", color: "#014c75", fontWeight: 600 }}
              onClick={() => navigate(`/customer/create-project?engagementId=${id}`)}
            >
              Create Project (Optional)
            </Button>
            <Button 
              type="primary" 
              size="large" 
              icon={<PlusOutlined />}
              style={{ background: "#01d9a9", borderColor: "#01d9a9", fontWeight: 600 }}
              onClick={() => navigate(`/customer/post-job?engagementId=${id}`)}
            >
              Create Job Requirement
            </Button>
          </div>
        </div>
      </div>

      {engagementHiringRecords.length > 0 && (
        <Card className="main-card" style={{ marginBottom: 24 }}>
          <div className="section-title" style={{ marginBottom: 20 }}>
            <TeamOutlined /> Active Contracts
            <Tag color="blue" style={{ marginLeft: 8, fontWeight: 600 }}>{contractStats.activeContracts}</Tag>
          </div>

          <div className="contracts-stats">
            <div className="stat-box">
              <div className="stat-label">Active Contracts</div>
              <div className="stat-value">{contractStats.activeContracts}</div>
              <div className="stat-sub">{contractJobGroups.length} job{contractJobGroups.length !== 1 ? "s" : ""}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Active Talents</div>
              <div className="stat-value">{contractStats.activeTalents}</div>
              <div className="stat-sub">across all roles</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Monthly Spend</div>
              <div className="stat-value" style={{ fontSize: 20 }}>{formatCurrency(contractStats.monthlySpend)}</div>
              <div className="stat-sub">per month</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Total Billed</div>
              <div className="stat-value" style={{ fontSize: 20 }}>{formatCurrency(contractStats.totalBilled)}</div>
              <div className="stat-sub">all time</div>
            </div>
          </div>

          <Table
            dataSource={contractJobGroups}
            rowKey="jobId"
            size="small"
            pagination={false}
            expandable={{
              expandedRowRender: (group) => (
                <div className="talent-sub-table">
                  <Table
                    dataSource={group.records}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    columns={[
                      { title: "Talent", dataIndex: "name", key: "name", render: (n) => <Text strong>{n}</Text> },
                      { title: "Experience", dataIndex: "experience", key: "exp", width: 120 },
                      { title: "Rate / mo", dataIndex: "monthlyRate", key: "rate", width: 130, render: (r) => formatCurrency(parseFloat(r) || 0) },
                      { title: "Total Billed", dataIndex: "totalBilled", key: "billed", width: 130, render: (b) => <Text strong style={{ color: "#014c75" }}>{formatCurrency(parseFloat(b) || 0)}</Text> },
                      { title: "Start Date", dataIndex: "onboardingDate", key: "start", width: 110, render: (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" }) : "-" },
                      { title: "Duration", dataIndex: "contractDuration", key: "dur", width: 100 },
                      {
                        title: "Expiry", dataIndex: "daysLeft", key: "days", width: 120,
                        render: (d) => (
                          <Tag color={d <= 0 ? "red" : d <= 30 ? "orange" : d <= 90 ? "gold" : "green"}>
                            {d <= 0 ? "Expired" : d <= 30 ? `${d}d — expiring soon` : `${d}d left`}
                          </Tag>
                        ),
                      },
                    ]}
                  />
                </div>
              ),
              rowExpandable: (group) => group.records.length > 0,
            }}
            columns={[
              {
                title: "Job Role",
                key: "jobTitle",
                render: (_, group) => (
                  <div>
                    <Text strong>{group.jobTitle}</Text>
                    <div><Text code style={{ fontSize: 11 }}>{group.jobId}</Text></div>
                  </div>
                ),
              },
              {
                title: "Talents", key: "count", width: 80, align: "center",
                render: (_, g) => <Tag color="blue">{g.records.length}</Tag>,
              },
              {
                title: "Monthly Spend", key: "monthly", width: 150,
                render: (_, g) => formatCurrency(g.records.reduce((s, hr) => s + (parseFloat(hr.monthlyRate) || 0), 0)),
              },
              {
                title: "Total Billed", key: "billed", width: 150,
                render: (_, g) => <Text strong style={{ color: "#014c75" }}>{formatCurrency(g.records.reduce((s, hr) => s + (parseFloat(hr.totalBilled) || 0), 0))}</Text>,
              },
              {
                title: "Months Active", key: "months", width: 120,
                render: (_, g) => {
                  const months = Math.max(0, ...g.records.map((hr) =>
                    Math.ceil((new Date() - new Date(hr.onboardingDate)) / (1000 * 60 * 60 * 24 * 30))
                  ));
                  return <span>{months > 0 ? `${months} mo` : "< 1 mo"}</span>;
                },
              },
              {
                title: "Next Expiry", key: "expiry", width: 130,
                render: (_, g) => {
                  const soonest = g.records.reduce((min, hr) => (!min || hr.daysLeft < min.daysLeft) ? hr : min, null);
                  if (!soonest) return "-";
                  return (
                    <Tag color={soonest.daysLeft <= 0 ? "red" : soonest.daysLeft <= 30 ? "orange" : soonest.daysLeft <= 90 ? "gold" : "green"}>
                      {soonest.daysLeft <= 0 ? "Expired" : `${soonest.daysLeft}d left`}
                    </Tag>
                  );
                },
              },
              {
                title: "", key: "action", width: 80,
                render: (_, g) => (
                  <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/customer/my-jobs/${g.jobId}`)}>
                    View
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      )}

      <Card className="main-card projects-section">
        <div className="section-header">
          <div className="section-title">
            <FolderOutlined /> Projects ({projects.length})
          </div>
        </div>

        {projects.length > 0 ? (
          <div className="project-cards">
            {projects.map((project) => (
              <div 
                key={project.id} 
                className="project-card"
                onClick={() => navigate(`/customer/project/${project.id}`)}
              >
                <div className="project-header">
                  <h4 className="project-title">{project.title}</h4>
                  <div className="project-actions">
                    <Tag color={projectStatusColors[project.status]}>
                      {project.status?.replace("_", " ").toUpperCase()}
                    </Tag>
                    <RightOutlined style={{ color: '#01d9a9', fontSize: 14 }} />
                  </div>
                </div>
                <div className="project-meta">
                  <div className="meta-item">
                    <span className="meta-label">Complexity:</span>
                    <span className="meta-value">{project.complexity || "Medium"}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Budget:</span>
                    <span className="meta-value">{formatCurrency(project.budgetAllocated)}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Jobs:</span>
                    <span className="meta-value">{project.totalJobs || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <FolderOutlined style={{ fontSize: 32, marginBottom: 12 }} />
            <div>No projects created yet</div>
            <Text type="secondary">Projects help you organize related job requirements together</Text>
          </div>
        )}
      </Card>

      <Card className="main-card jobs-section">
        <div className="section-header">
          <div className="section-title">
            <TeamOutlined /> Jobs ({allJobs.length})
          </div>
        </div>

        {allJobs.length > 0 ? (
          <Tabs
            className="jobs-tabs"
            defaultActiveKey="all"
            items={[
              {
                key: "all",
                label: `All Jobs (${allJobs.length})`,
                children: (
                  <Table
                    className="job-table"
                    columns={jobColumns}
                    dataSource={allJobs}
                    rowKey="id"
                    pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
                    size="small"
                  />
                ),
              },
              {
                key: "project",
                label: `Project Jobs (${projectJobs.length})`,
                children: (
                  <Table
                    className="job-table"
                    columns={jobColumns}
                    dataSource={projectJobs}
                    rowKey="id"
                    pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
                    size="small"
                  />
                ),
              },
              {
                key: "direct",
                label: `Direct Jobs (${directJobs.length})`,
                children: (
                  <Table
                    className="job-table"
                    columns={jobColumns}
                    dataSource={directJobs}
                    rowKey="id"
                    pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
                    size="small"
                  />
                ),
              },
            ]}
          />
        ) : (
          <div className="empty-state">
            <TeamOutlined style={{ fontSize: 32, marginBottom: 12 }} />
            <div>No job requirements created yet</div>
            <Text type="secondary">Post job requirements to start finding the right talent</Text>
          </div>
        )}
      </Card>

      <Modal
        title="Request Engagement Closure"
        open={closureModalVisible}
        onCancel={() => setClosureModalVisible(false)}
        onOk={handleRequestClosure}
        okText="Submit Request"
        okButtonProps={{ loading: submitting, danger: true }}
      >
        <Paragraph>
          Are you sure you want to request closure for this engagement? This will notify the admin for approval.
        </Paragraph>
        <TextArea
          rows={4}
          placeholder="Please provide a reason for closing this engagement..."
          value={closureReason}
          onChange={(e) => setClosureReason(e.target.value)}
        />
      </Modal>
    </DetailsWrapper>
  );
};

export default CustomerEngagementDetails;
